import {atom, type Atom} from 'jotai';
import type {GenType} from '@features/flow/flowService';

/**
 * Stores detected genType per screen during streaming.
 * This provides interim state before the structure is created.
 *
 * Keyed by screenId to support parallel generation with different genTypes:
 * - Screen A streaming at 375px (mobile)
 * - Screen B streaming at 1440px (web)
 */
export const streamingGenTypeByScreenAtom = atom<
  Partial<Record<string, GenType>>
>({});

/**
 * Per-screen atom cache for selective subscription.
 * Use streamingGenTypeAtom(screenId) in components to avoid re-rendering
 * when other screens' genTypes change.
 */
const streamingGenTypeAtomCache = new Map<string, Atom<GenType | undefined>>();

export function streamingGenTypeAtom(
  screenId: string,
): Atom<GenType | undefined> {
  const cached = streamingGenTypeAtomCache.get(screenId);
  if (cached) {
    return cached;
  }
  const newAtom = atom((get) => get(streamingGenTypeByScreenAtom)[screenId]);
  streamingGenTypeAtomCache.set(screenId, newAtom);
  return newAtom;
}

/**
 * Helper atom to set streaming genType for a screen.
 * Call this before starting generation to ensure correct frame dimensions.
 */
export const setStreamingGenTypeAtom = atom(
  null,
  (get, set, {screenId, genType}: {screenId: string; genType: GenType}) => {
    set(streamingGenTypeByScreenAtom, {
      ...get(streamingGenTypeByScreenAtom),
      [screenId]: genType,
    });
  },
);

/**
 * Clear streaming genType for a screen after generation completes.
 * Prevents stale streaming genType from overriding structure genType
 * when switching between versions via History panel.
 */
export const clearStreamingGenTypeAtom = atom(
  null,
  (get, set, screenId: string) => {
    const {[screenId]: _, ...rest} = get(streamingGenTypeByScreenAtom);
    set(streamingGenTypeByScreenAtom, rest);
  },
);
